import { optimizedCompletion } from '@/lib/openai/speedOptimizer';
import { ApiError } from '@/lib/errors';



// Skills Assessment Generation AI Service
export async function generateSkillsAssessment(
  assessmentName: string,
  skills: string[],
  assessmentType: string,
  difficulty: string,
  questionCount: number = 10
): Promise<any> {
  try {
    const response = await optimizedCompletion(
      'skills-assessment',
      [
        {
          role: 'system',
          content: `You are an expert assessment designer and technical recruiter. 
          Generate a comprehensive skills assessment with realistic, practical questions.
          
          For technical skills: Include coding challenges, multiple choice, and scenario-based questions
          For soft skills: Focus on behavioral scenarios and situational judgment
          For cognitive skills: Include problem-solving and analytical reasoning
          
          Each question should have:
          - Clear, specific question text
          - Appropriate difficulty level
          - Correct answer(s)
          - Point value
          - Explanation for the correct answer
          
          Format as JSON:
          {
            "questions": [
              {
                "id": "q1",
                "type": "multiple_choice|code_challenge|scenario|essay",
                "question": "question text",
                "options": ["option1", "option2", "option3", "option4"], // for multiple choice
                "correct": "correct answer or option",
                "points": number,
                "explanation": "why this is correct",
                "timeLimit": number, // in minutes, optional
                "template": "code template", // for code challenges
                "expectedOutput": "expected result" // for code challenges
              }
            ]
          }`
        },
        {
          role: 'user',
          content: `Generate a ${difficulty} level ${assessmentType} assessment called "${assessmentName}" 
          with ${questionCount} questions testing these skills: ${skills.join(', ')}.
          
          Make the questions practical, realistic, and appropriate for the ${difficulty} difficulty level.
          Include a variety of question types suitable for ${assessmentType} assessment.`
        }
      ],
      { useCache: true }
    );

    const content = response.choices[0]?.message?.content;
    if (!content) {
      throw new Error('No response from AI');
    }

    return JSON.parse(content);
  } catch (error) {
    if (error instanceof Error) {
      throw new ApiError(error.message, 500);
    }
    throw new ApiError('Failed to generate skills assessment', 500);
  }
}

// Communication Template Generation AI Service
export async function generateEmailTemplate(
  templateType: string,
  context: any
): Promise<any> {
  try {
    const response = await optimizedCompletion(
      'text-generation',
      [
        {
          role: 'system',
          content: `You are an expert in professional communication and HR best practices. 
          Generate professional, personalized email templates for different stages of the hiring process.
          
          The template should:
          - Use professional, warm tone
          - Include appropriate variables for personalization
          - Follow email best practices
          - Be ready to use immediately
          
          Format as JSON:
          {
            "subject": "email subject with {{variables}}",
            "content": "email body with {{variables}} for personalization",
            "variables": ["variable1", "variable2"],
            "category": "acknowledgment|screening|interview|rejection|offer",
            "timing": "immediate|scheduled|manual"
          }`
        },
        {
          role: 'user',
          content: `Generate a ${templateType} email template with this context: ${JSON.stringify(context)}`
        }
      ],
      { useCache: true }
    );

    const content = response.choices[0]?.message?.content;
    if (!content) {
      throw new Error('No response from AI');
    }

    return JSON.parse(content);
  } catch (error) {
    if (error instanceof Error) {
      throw new ApiError(error.message, 500);
    }
    throw new ApiError('Failed to generate email template', 500);
  }
} 